import { Gift, Sparkles, Trophy } from "lucide-react";
import { motion } from "framer-motion";
import { Trans, useTranslation } from "react-i18next";
import { fmtCredits } from "../lib/api";

/**
 * Progreso hacia el siguiente premio, bajo la cabecera.
 *
 * El contador del canal (TabCanales, insight.milestoneTarget) llega a 12 y para; los premios son
 * infinitos y 70 casillas no caben en una franja de 40px. Aquí el raíl es SIEMPRE de ancho fijo y
 * lo que cambia es cuánto pasado enseña: el tramo vivo en móvil/tablet, cuatro tramos en
 * escritorio. Las dos vistas son el mismo `Rail` con distinto `count`, así que numeran igual.
 *
 * Numeración absoluta y real (70, 71, 72) y NUNCA 1-2-3: un 1-2-3 hace que el raíl de quien lleva
 * 70 vídeos sea idéntico al de quien lleva 4, y tira la única prueba de recorrido que tenemos.
 * Pero solo se dicen DOS números — dónde estás y qué persigues. Los demás son puntos, y los
 * premios ya cobrados son un regalo sin número: ya no hay nada que perseguir en ellos.
 *
 * Ninguna cifra de la tabla de tramos vive aquí (ver LOGRO_TRAMOS en server.py). El chip de subida
 * solo aparece si el servidor manda `proximo_logro.mejora`; sin ese campo no se promete nada.
 */

/** Casilla del raíl. Todo el reparto de tamaños vive aquí para que las dos vistas no diverjan. */
function Slot({ n, cada, hito, total, ultimo, creditos, t }) {
  const done = n <= total;
  const esHito = n % cada === 0;

  // El premio vivo: el único elemento grande, y el único hito que dice su número. Es lo que se
  // persigue, así que es lo que se ve primero.
  if (n === hito) {
    return (
      <motion.span
        title={t("logros.slotProximo", { n, creditos: fmtCredits(creditos) })}
        className={`flex items-center gap-1 h-8 pl-1.5 pr-2 rounded-full border-2 shrink-0 ${
          ultimo
            ? "bg-emerald-500 border-emerald-500 text-white ring-2 ring-emerald-300 ring-offset-2 ring-offset-emerald-50"
            : "bg-white border-emerald-400 text-emerald-700"}`}
        animate={ultimo ? { scale: [1, 1.06, 1] } : false}
        transition={ultimo ? { duration: 1.9, repeat: Infinity, ease: "easeInOut" } : undefined}>
        <Gift size={15} strokeWidth={2.5} className={ultimo ? "" : "text-emerald-500"} />
        <span className="text-[11px] font-extrabold tabular-nums">{n}</span>
      </motion.span>
    );
  }

  // Hitos que no son el vivo. Solo pueden ser pasados (cobrados) o, si aún no has llegado al 12,
  // futuros: la escalera que queda por delante, que es justo lo que anima a un usuario nuevo.
  if (esHito) {
    return done ? (
      <span title={t("logros.slotGanado", { n })}
        className="grid place-items-center h-[22px] w-[22px] rounded-full bg-emerald-600 text-white
                   shrink-0 cursor-default transition-transform duration-150 hover:scale-125">
        <Gift size={12} strokeWidth={2.5} />
      </span>
    ) : (
      <span className="grid place-items-center h-[22px] w-[22px] rounded-full border-2 shrink-0
                       border-emerald-200 bg-white text-emerald-300">
        <Gift size={12} strokeWidth={2.5} />
      </span>
    );
  }

  // Dónde estás ahora: el único vídeo corriente que merece decir su número.
  if (n === total) {
    return (
      <span className="grid place-items-center h-6 min-w-[1.5rem] px-1 rounded-full shrink-0
                       bg-emerald-600 text-white text-[10px] font-extrabold tabular-nums
                       ring-2 ring-emerald-200">
        {n}
      </span>
    );
  }

  return <span className={`h-2 w-2 rounded-full shrink-0 ${done ? "bg-emerald-500" : "bg-emerald-200"}`} />;
}

function Rail({ desde, count, denso, cada, hito, total, ultimo, creditos, t }) {
  const numeros = Array.from({ length: count }, (_, i) => desde + i + 1);
  // Acabas de cobrar un premio: el raíl pinta el tramo SIGUIENTE y tu vídeo actual se queda fuera.
  // Se antepone como ancla para que el "vas por el N" no desaparezca nunca. En escritorio no pasa:
  // la ventana se ancla al hito y siempre contiene la posición actual.
  const anclaFuera = total > 0 && total <= desde;
  const conector = denso ? "w-1.5" : "w-3";

  return (
    <ol className="flex items-center shrink-0" aria-hidden="true">
      {anclaFuera && (
        <li className="flex items-center">
          <span className="flex items-center gap-1 h-6 px-1.5 rounded-full bg-emerald-600 text-white
                           text-[10px] font-extrabold tabular-nums ring-2 ring-emerald-200 shrink-0">
            {total % cada === 0 && <Gift size={11} strokeWidth={2.5} />}{total}
          </span>
        </li>
      )}
      {numeros.map((n, i) => (
        <li key={n} className="flex items-center">
          {(i > 0 || anclaFuera) && (
            <span className={`h-[3px] ${conector} ${n - 1 <= total ? "bg-emerald-500" : "bg-emerald-200"}`} />
          )}
          <Slot n={n} cada={cada} hito={hito} total={total} ultimo={ultimo} creditos={creditos} t={t} />
        </li>
      ))}
      {/* Lo único que cuenta que después de este premio hay otro, y otro, para siempre. */}
      <li className="flex items-center">
        <span className={`${denso ? "w-3" : "w-4"} border-t-2 border-dashed border-emerald-300`} />
        <Gift size={13} className="text-emerald-400/70 shrink-0" />
      </li>
    </ol>
  );
}

export default function LogrosRail({ quota }) {
  const { t } = useTranslation();
  const pl = quota?.proximo_logro;
  if (!pl?.hito) return null;

  const cada = pl.cada || 3;
  // Se acota: un desajuste de recuento no puede pintar 4 casillas de 3 ni encenderlas todas
  // mientras el texto dice que faltan.
  const faltan = Math.min(cada, Math.max(1, pl.faltan || cada));
  const hito = pl.hito;
  const total = hito - faltan;   // derivado del mismo objeto: no puede desincronizarse de la frase
  const ultimo = faltan === 1;   // el estado que de verdad tira: se trata distinto
  const mejora = pl.mejora || null;
  const ganados = quota?.logros_ganados;

  // Ventana de escritorio anclada al HITO VIVO, no al bloque absoluto de 12: así los regalos caen
  // clavados al final de cada tramo y tu posición siempre está dentro. Con el bloque absoluto,
  // quien tuviera 72 vídeos exactos (hito 75) veía la ventana 73-84 y se quedaba FUERA del raíl.
  // Se mide en tramos y no en casillas para que aguante si `cada` deja de ser 3 (es regla de
  // servidor, puede cambiar).
  const tramosVisibles = Math.max(2, Math.min(4, Math.floor(12 / cada)));
  const baseDesktop = Math.max(0, hito - cada * tramosVisibles);
  const ctx = { cada, hito, total, ultimo, creditos: pl.creditos, t };
  const leyenda = t("logros.railAria", { total, hito, creditos: fmtCredits(pl.creditos) });

  return (
    <div data-testid="logros-banner" className="bg-emerald-50 border-b border-emerald-200/80">
      {/* En móvil todo va en UNA línea (flex-nowrap): la frase larga partía el aviso en dos o tres
          renglones y una franja de 90px de alto sobre el contenido es demasiado para algo que solo
          informa. Desde sm vuelve el wrap normal, porque ahí ya cabe entero. */}
      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-2.5 flex flex-nowrap sm:flex-wrap items-center gap-x-2 sm:gap-x-4 gap-y-2">

        {/* Late solo cuando falta uno: una animación permanente acaba siendo papel pintado. */}
        {/* Oculto en móvil: el raíl ya termina en iconos de regalo y el hueco (34px) lo necesita
            la frase. Anunciar el premio dos veces en 375px es un lujo que no cabe. */}
        <motion.span
          className={`hidden sm:grid shrink-0 place-items-center h-8 w-8 rounded-xl ${
            ultimo ? "bg-emerald-500 text-white shadow-sm shadow-emerald-600/30"
                   : "bg-emerald-100 text-emerald-600"}`}
          animate={ultimo ? { rotate: [0, -8, 8, -4, 0] } : false}
          transition={ultimo ? { duration: 1.4, repeat: Infinity, repeatDelay: 3.5, ease: "easeInOut" } : undefined}
          aria-hidden="true">
          <Gift size={17} strokeWidth={2.5} />
        </motion.span>

        {/* Dos redacciones de lo MISMO, no dos mensajes distintos: la corta conserva los dos
            números que importan (cuánto falta y cuánto pagan) y suelta el resto de la frase, que
            es lo único que no cabe. La larga vuelve en cuanto hay sitio. */}
        <p className={`sm:hidden text-[clamp(11px,3.2vw,14px)] font-bold whitespace-nowrap ${ultimo ? "text-emerald-950" : "text-emerald-900"}`}>
          <Trans i18nKey="logros.subheaderCorto" count={faltan}
                 values={{ creditos: fmtCredits(pl.creditos) }}
                 components={{ b: <b className="font-extrabold" /> }} />
        </p>

        {/* Lo gordo es el premio, no la frase. El <b> lo coloca la traducción para que cada idioma
            decida qué palabra carga el peso. */}
        <p className={`hidden sm:block text-base min-w-0 ${ultimo ? "text-emerald-950" : "text-emerald-900"}`}>
          <Trans i18nKey="logros.subheader" count={faltan}
                 values={{ creditos: fmtCredits(pl.creditos) }}
                 components={{ b: <b className="font-extrabold" /> }} />
        </p>

        {/* Tramo vivo: móvil y tablet. ~90px, entra de sobra en 320px. En móvil se empuja a la
            derecha (ml-auto) para que la línea quede icono+texto a un lado y raíl al otro, en vez
            de todo amontonado a la izquierda con un hueco muerto al final. */}
        <div className="lg:hidden ml-auto sm:ml-0" title={leyenda}>
          <Rail desde={hito - cada} count={cada} denso={false} {...ctx} />
        </div>

        {/* Cuatro tramos: escritorio. ~240px. Añade lo que el tramo solo no puede contar —
            los premios YA cobrados, en sólido. Esa es la mitad del bucle que el móvil no da:
            no solo "lo que viene", también "esto ya me ha pagado tres veces hace nada". */}
        <div className="hidden lg:block" title={leyenda}>
          <Rail desde={baseDesktop} count={cada * tramosVisibles} denso {...ctx} />
        </div>

        {/* Los raíles son decorativos; el significado íntegro va aquí, una sola vez. */}
        <span className="sr-only">{leyenda}</span>

        <div className="flex items-center gap-3 sm:ml-auto shrink-0">
          {mejora ? (
            <span className="hidden xl:inline-flex items-center gap-1.5 rounded-full bg-white
                             border border-emerald-200 px-2.5 py-1 text-xs font-semibold text-emerald-700">
              <Sparkles size={13} className="text-emerald-500" />
              {t("logros.mejora", { hito: mejora.hito, creditos: fmtCredits(mejora.creditos) })}
            </span>
          ) : pl.tope ? (
            <span className="hidden xl:inline-flex items-center gap-1.5 rounded-full bg-emerald-100
                             px-2.5 py-1 text-xs font-semibold text-emerald-800">
              <Trophy size={13} className="text-emerald-600" />
              {t("logros.tope")}
            </span>
          ) : null}

          {!!ganados?.hitos && (
            <span className="hidden md:inline text-sm text-emerald-700">
              {t("logros.ganado", { creditos: fmtCredits(ganados.creditos), count: ganados.hitos })}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
